<template>
	<div class="game-screen paw-bg">
		<div class="end-screen">
			<ScoreMedallion :badge="true" class="score-badge"/>
			<div class="content">
				<h1 class="primary-heading" v-html="ui.heading"></h1>
			</div>
			<div class="cat-bg-group">
				<div class="content">
					<SocialShare />
					<h1 v-html="ui.section_heading"></h1>
					<div class="metaphors">
						<div
							class="metaphor media"
							v-for="(metaphor, index) in ui.metaphors"
							:key="index"
							ref="metaphors"
						>
						>
							<img class="media-figure" :src="metaphor.icon.url" />
							<div class="media-body">
								<h4 class="heading">{{ metaphor.heading }}</h4>
								<div class="description" v-html="metaphor.text"></div>
							</div>
						</div>
					</div>
					<div class="footer-group">
						<div class="learn-more" ref="content" v-html="ui.explanation"></div>
						<div class="sponsorship">
							<span class="sponsor-text">{{ ui.sponsor_text }}</span>
							<img class="sponsor-logo" src="@assets/img/ngpf-fulltext.svg" />
						</div>
						<Button :action="replay" ref="replayBtn">{{ ui.replay_button_text }}</Button>
					</div>
				</div>
			</div>
			<div class="cat-debt-banner" ref="debtBanner">
				<div class="media">
					<img class="media-figure" src="@assets/img/icon-angel.svg" />
					<div class="media-body">
						<h3 class="cat-debt">
							{{ ui.cat_debt_label }}
							<span class="debt-count">{{ catDebt }}</span>
						</h3>
						<span class="debt-aside">{{ ui.cat_debt_aside }}</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import 'intersection-observer';
import SocialShare from '@/components/abstract/SocialShare';
import ScoreMedallion from '@/components/abstract/ScoreMedallion';
import Button from '@/components/abstract/Button';
import { mapState, mapActions } from 'vuex';

export default {
	name: 'EndScreen',
	components: {
		Button,
		ScoreMedallion,
		SocialShare,
	},
	props: ["isTransitioning"],
	data() {
		return {
			openMetaphor: false,
			openMetaphorIndex: -1,
			catDebt: "XXX,XXX",
		};
	},
	computed: {
		...mapState({
			ui: state => state.config.info.post_game,
			score: state => state.score.score,
		}),
	},
	methods: {
		...mapActions([
			"sendEvent",
		]),
		createObserver(element, label) {
			const observer = new IntersectionObserver(this.logVisibilityEvent(element, label), {
				root: this.$el,
				threshold: 1.0,
			});
			observer.observe(element);
		},
		logVisibilityEvent(element, label) {
			return (entries, obsever) => {
				if (entries[0].isIntersecting) {
					obsever.unobserve(element);
					this.sendDynamicEvent("Visibility", label);
				}
			};
		},
		sendDynamicEvent(action, label) {
			this.sendEvent({
				ec: "Post-Game",
				ea:	action,
				el: label,
			});
		},
		logContentEvent(event) {
			if (event.path.find(tag => tag.tagName === "A")) {
				this.sendDynamicEvent("Learn More", event.path[0].innerText);
			}
		},
		replay() {
			this.sendDynamicEvent("Replay", "Replay");
			location.reload();
		},
		async updateCatDebt() {
			const response = await this.getCatDebt();
			this.catDebt = response.debt;
		},
		getCatDebt() {
			return this.$http.get('/wp-json/dev-api/v1/cat-debt/').then(
				response => Promise.resolve(response.body),
				response => Promise.reject(response.body),
			);
		},
	},
	mounted() {
		this.updateCatDebt();
		this.$nextTick(() => {
			this.$refs.content.addEventListener("click", this.logContentEvent);
			this.createObserver(this.$refs.metaphors[this.$refs.metaphors.length - 1], "Boxes");
			this.createObserver(this.$refs.replayBtn.$el, "Replay Button");
			this.createObserver(this.$refs.debtBanner, "Cat Debt Banner");
		});
	},
};
</script>

<style lang="scss" scoped>

/* =====================================================================
	Scoped Vars
	==================================================================== */

	$metaphor-height: 130px;
	$metaphor-media-padding: 20px;

	$metaphor-mobile-height: 100px;

/* =====================================================================
	General
	==================================================================== */

	html, body {
		height: 100%;
		-webkit-overflow-scrolling: touch;
		touch-action: auto;
	}

	.game-screen {
		overflow-y: scroll;
		padding-top: 7.5vh;
		padding-bottom: 15vh;
		align-items: flex-start;
	}

	.end-screen {
		display: flex;
		height: auto;
		width: 100%;
		align-items: center;
		flex-direction: column;
	}

	h1 {
		color: $brand-color-alt-darker;
		font-family: $headline-font-stack;
		text-transform: uppercase;
		font-weight: 700;
		font-size: 53px;
		line-height: 1.2;
		margin-bottom: 2vh;

		@include tablet {
			padding: 0 100px;
		}

		@include mobile {
			font-size: 32px;
		}
	}

	h3 {
		font-family: $headline-font-stack;
		text-transform: uppercase;
		font-weight: 700;
		font-size: 29px;
		line-height: 1.2;
		margin-bottom: 0;
	}

	p {
		font-weight: 400;
		letter-spacing: .025em;
		line-height: 1.2;
	}

	.primary-heading {
		margin-bottom: 1em;
	}

/* =====================================================================
	Explanation Group
	==================================================================== */

	.cat-bg-group {
		display: flex;
		width: 100%;
		padding: 5vh 0;
		align-items: flex-start;
		justify-content: center;
		background-color: $brand-color-primary-lighter;
		position: relative;
		margin-top: 165px;

		@include mobile {
			margin-top: 150px;
		}

		@include mobile {
			padding: 0 1em;
		}

		h1 {
			color: $brand-color-primary-dark;
		}

		.content {
			max-width: 1050px;
			@include mobile {
				padding: 40px 0;
			}
		}

		&::after,
		&::before {
			content: url('~@assets/img/cat-silhouette.svg');
			display: block;
			width: 250px;
			height: 165px;
			position: absolute;
			bottom: 100%;

			@include mobile {
				height: 80px;
				width: 120px;
			}
		}

		&::before {
			right: 70%;
		}

		&::after {
			left: 70%;
			transform: rotateY(180deg);
		}

		.social-share {
			position: absolute;
			top: -165px;
			left: 0;
			right: 0;

			@include mobile {
				top: -150px;
			}
		}
	}

/* =====================================================================
	Metaphors
	==================================================================== */

	.metaphors {
		display: flex;
		justify-content: space-around;
		flex-wrap: wrap;
		align-items: flex-start;
	}

	.metaphor {
		width: 100%;
		max-width: 475px;
		height: $metaphor-height;
		align-items: center;
		margin-top: 3vh;
		position: relative;
		transition: all $ui-transition-duration;
		border-radius: $metaphor-height / 2;
		background-color: $brand-color-primary-dark;
		text-align: left;

		@include mobile {
			align-items: center;
			height: $metaphor-mobile-height;
		}

		.heading {
			font-family: 'Asap Condensed';
			text-transform: uppercase;
			color: $brand-color-alt-darker;
			line-height: 1;
			margin-bottom: .25em;
			letter-spacing: .07em;

			@include desktop-sm {
				white-space: nowrap;
			}
		}

		.media-body {
			padding: 1em 2em 1em $metaphor-height + $metaphor-media-padding;

			@include mobile {
				padding: 1em 2em 1em 110px;
			}
		}

		.media-figure {
			height: $metaphor-height;
			position: absolute;
			top: 0;
			right: 100%;
			margin: 0;
			transform: translateX($metaphor-height);

			@include mobile {
				height: $metaphor-mobile-height;
				transform: translateX($metaphor-mobile-height);
			}
		}

		.description /deep/ p {
			line-height: 1.3;
			margin-bottom: 0;
			color: $brand-color-primary-extra-light;
		}
	}

/* =====================================================================
	Footer Group
	==================================================================== */

	.footer-group {
		display: flex;
		flex-wrap: wrap;
		max-width: 600px;
		align-items: center;
		justify-content: space-around;
		margin: 1em auto;

		@include mobile {
			flex-direction: column;
		}
	}

	.learn-more {
		width: 100%;
		margin: 2em auto;
		font-size: 24px;
		font-weight: 600;
		color: $brand-color-primary-dark;

		@include mobile {
			line-height: 1.3;
			padding-left: 5px;
			padding-right: 5px;
		}
	}

	.sponsorship {
		height: 1em;
		display: flex;
		align-items: center;
		justify-content: center;
		letter-spacing: .07em;
		text-transform: uppercase;
		font-weight: 700;
		font-size: 150%;

		@include mobile {
			margin-bottom: 1em;
		}
	}

	.sponsor-logo {
		display: inline-block;
		width: 120px;
		margin-left: .75ch;

		@include mobile {
			width: 80px;
		}
	}

	.sponsor-text {
		line-height: 1;
		font-size: 22px;

		@include mobile {
			font-size: 18px;
		}
	}

/* =====================================================================
	Debt Banner
	==================================================================== */

	.cat-debt-banner {
		display: flex;
		width: 100%;
		padding: 1vh 0;
		justify-content: center;
		background-color: $brand-color-alt-darker;
		color: $brand-color-primary-dark;
		white-space: nowrap;

		@include mobile {
			padding: 0 1em;
		}

		.media {
			align-items: center;

			@include mobile {
				width: 100%;
			}
		}

		.media-figure {
			height: 100px;
			transform: scale(2) translateX(-25%);

			@include mobile {
				height: 75px;
				margin-left: 1em;
				margin-right: .5em;
				transform: scale(1.25) translateX(-12.5%);
			}
		}

		h3 {
			@include mobile {
				font-size: 22px;
			}
		}

		.debt-count {
			@include mobile {
				display: block;
			}
		}
	}

	/deep/ .score-badge.score-group {
		position: absolute;
		top: 1vmin;
		right: 1vmin;

		@include mobile {
			position: relative;
			top: 0;
			left: 0;
		}
	}

</style>



// WEBPACK FOOTER //
// src/components/views/EndScreen.vue